/* eslint-disable no-param-reassign */

import { getVideo } from './loadVideoElement';

// How long to wait for a seek to resolve an unknown duration before giving up. Generous: this only runs
// for videos whose duration the browser could not report up front, and it blocks submission.
const DURATION_SEEK_TIMEOUT_MS = 3000;

/**
 * Reads the duration of a video, in seconds.
 *
 * `MediaRecorder` writes no duration into the container header, so browsers report `Infinity` for
 * recorded blobs until the element is seeked. Seeking to the end forces the real value to be resolved.
 * Returns a non-finite number when even that fails — callers decide whether that is fatal. See
 * CHANGE-97165.
 */
export async function getVideoDuration(file: Blob, video: HTMLVideoElement): Promise<number> {
	const loadedVideo = await getVideo(file, video);

	if (Number.isFinite(loadedVideo.duration)) {
		return loadedVideo.duration;
	}

	return resolveDurationBySeeking(loadedVideo);
}

async function resolveDurationBySeeking(video: HTMLVideoElement): Promise<number> {
	// eslint-disable-next-line promise/avoid-new
	return new Promise((resolve) => {
		const cleanUp = () => {
			video.removeEventListener('durationchange', handleDurationChange);
			clearTimeout(timeout);
		};

		const handleDurationChange = () => {
			if (!Number.isFinite(video.duration)) {
				return;
			}
			cleanUp();
			resolve(video.duration);
		};

		const timeout = setTimeout(() => {
			cleanUp();
			resolve(video.duration);
		}, DURATION_SEEK_TIMEOUT_MS);

		video.addEventListener('durationchange', handleDurationChange);

		try {
			video.currentTime = Number.MAX_SAFE_INTEGER;
		} catch {
			cleanUp();
			resolve(video.duration);
		}
	});
}
